import {useState} from 'react'
import {Link, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {DataTable, type Column} from '@/components/data-table'
import {CifraModal} from '@/components/cifra-modal'
import {CifraProcedencia} from '@/components/cifra-procedencia'
import {SolicitudDetalle} from '@/components/solicitud-detalle'
import {PrepararDialog} from '@/components/solicitudes-ips/preparar-dialog'
import {HelpIcon} from '@/components/help-icon'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {PlanLockedPage} from '@/components/plan-upsell'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {CeldaActo, CeldaEstado} from '@/components/ui/celdas-tabla'
import {FiltrosDeEstado, type OpcionEstado} from '@/components/ui/filtros-de-estado'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {useOperatorPage} from '@/components/no-access-state'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useTableState} from '@/hooks/common/use-table-state'
import {useIsMobile} from '@/hooks/common/use-media-query'
import {useCifras, type Cifra} from '@/hooks/empresa/organizacion/use-cifras'
import {
  CARGOS_PAGE_SIZE,
  ESTADO_TONE,
  SOLICITUDES_PAGE_SIZE,
  useCargosListos,
  useResumenCargos,
  useSolicitudesIps,
  type CargoListo,
  type CargosListos,
  type LoteSolicitudes,
  type SolicitudIps,
} from '@/hooks/salud/medicina_laboral/use-solicitudes-ips'
import {translateDetail} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {SearchInput} from '@/components/ui/search-input'

const PLAZOS = ['plazo_corto_habiles', 'plazo_largo_habiles']

/** Los dos plazos con su origen y la fecha en que caerían hoy: cinco días hábiles no son cinco días (PQ-1). */
function Plazos({
  businessId,
  vence,
  puedeFijar,
}: {
  businessId: string
  vence: {corto: string; largo: string}
  puedeFijar: boolean
}) {
  const {t} = useTranslation()
  const {data} = useCifras(businessId)
  const [editando, setEditando] = useState<Cifra | null>(null)
  const cifras = (data?.cifras ?? []).filter((cifra) => PLAZOS.includes(cifra.cifra_key))

  return (
    <div className="surface space-y-3 rounded-xl p-4" data-testid="solicitudIps-plazos">
      <h2 className="font-mono text-xs tracking-wider text-muted-foreground uppercase">
        {t('solicitudIps.plazos.title')}
      </h2>
      {cifras.map((cifra) => (
        <div key={cifra.cifra_key} className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            <p className="text-sm">
              {t(`solicitudIps.plazos.${cifra.cifra_key}`, {
                dias: cifra.valor_texto ?? '',
                fecha: formatDateOnly(cifra.cifra_key === 'plazo_corto_habiles' ? vence.corto : vence.largo),
              })}
            </p>
            <CifraProcedencia cifra={cifra} />
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={!puedeFijar}
            data-testid={`solicitudIps-fijar-${cifra.cifra_key}`}
            onClick={() => setEditando(cifra)}>
            {t('solicitudIps.plazos.fijar')}
          </Button>
        </div>
      ))}
      <CifraModal
        open={editando !== null}
        onOpenChange={(abierto) => !abierto && setEditando(null)}
        businessId={businessId}
        cifra={editando}
      />
    </div>
  )
}

type TabKey = 'solicitudes' | 'preparar' | 'plazos'

const TABS: TabKey[] = ['solicitudes', 'preparar', 'plazos']

function parseTab(value: string | null): TabKey | null {
  return TABS.find((clave) => clave === value) ?? null
}

/** react-query se rinde en silencio tras dos reintentos: sin esto la pestaña queda vacía para siempre. */
function ErrorDeCarga({onReintentar}: {onReintentar: () => void}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-3">
      <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="solicitudIps-error-carga">
        {t('solicitudIps.errorCarga')}
      </p>
      <Button size="sm" variant="outline" onClick={onReintentar}>
        {t('solicitudIps.reintentar')}
      </Button>
    </div>
  )
}

function SolicitudesTab({businessId, puedeFirmar}: {businessId: string; puedeFirmar: boolean}) {
  const {t} = useTranslation()
  const table = useTableState('radicado')
  const lista = useSolicitudesIps(businessId, {
    page: table.page,
    search: table.search,
    sortBy: table.sortBy,
    sortDir: table.sortDir,
  })
  const [expandido, setExpandido] = useState<string | null>(null)

  const columns: Column<SolicitudIps>[] = [
    {
      key: 'radicado',
      header: t('solicitudIps.columnas.radicado'),
      sortable: true,
      cell: (fila) => <span className="font-mono text-xs">{fila.radicado}</span>,
    },
    {
      key: 'trabajador',
      header: t('solicitudIps.columnas.trabajador'),
      width: 'w-1/4',
      cell: (fila) => <span className="text-sm">{fila.trabajador}</span>,
    },
    {key: 'cargo', header: t('solicitudIps.columnas.cargo'), cell: (fila) => fila.cargo},
    {
      key: 'tipo',
      header: t('solicitudIps.columnas.tipo'),
      cell: (fila) => <CeldaEstado chip={<Tag tone="neutral">{t(`solicitudIps.tipo.${fila.tipo}`)}</Tag>} />,
    },
    {
      key: 'estado',
      header: t('solicitudIps.columnas.estado'),
      headerAside: (
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('solicitudIps.columnas.estado')})}
          testId="solicitudIps-estado-help">
          {t('solicitudIps.columnas.estadoHelp')}
        </HelpIcon>
      ),
      sortable: true,
      cell: (fila) => (
        <CeldaEstado
          chip={<Tag tone={ESTADO_TONE[fila.estado]}>{t(`solicitudIps.estado.${fila.estado}`)}</Tag>}
          porque={
            fila.estado === 'sin_confirmacion_entrega'
              ? t('solicitudIps.estadoPorque.sinConfirmacionEntrega')
              : undefined
          }
        />
      ),
    },
  ]

  if (lista.isError) return <ErrorDeCarga onReintentar={() => void lista.refetch()} />

  return (
    <ListCard
      testId="solicitudIps-lista"
      toolbar={
        <SearchInput
          value={table.searchInput}
          onChange={table.setSearchInput}
          placeholder={t('solicitudIps.buscar')}
          data-testid="solicitudIps-buscar"
        />
      }
      footer={
        (lista.data?.total ?? 0) > 0 && (
          <PaginationBar
            page={table.page}
            pageSize={SOLICITUDES_PAGE_SIZE}
            total={lista.data!.total}
            onPageChange={table.setPage}
          />
        )
      }>
      <DataTable
        columns={columns}
        rows={lista.data?.items ?? []}
        rowKey={(fila) => fila.id}
        sortBy={table.sortBy}
        sortDir={table.sortDir}
        onSort={table.toggleSort}
        onRowClick={(fila) => setExpandido(expandido === fila.id ? null : fila.id)}
        renderRowDetail={(fila) =>
          fila.id === expandido ? (
            <SolicitudDetalle solicitud={fila} businessId={businessId} puedeFirmar={puedeFirmar} />
          ) : null
        }
        // Con `body`: el vacío es la pestaña entera, el único caso donde U7 lo permite.
        emptyState={<ListEmptyState page title={t('solicitudIps.vacio.title')} body={t('solicitudIps.vacio.body')} />}
      />
    </ListCard>
  )
}

/** `listo` y no `listos`: es el nombre del parámetro del servidor y del testid del chip. */
type FiltroCargos = 'todos' | 'listo' | 'incompletos'

/** Generar es acto de firmante: el botón se apaga con la razón que calculó la API, nunca en silencio. */
function PrepararTab({
  businessId,
  puedeFirmar,
  resumen,
}: {
  businessId: string
  puedeFirmar: boolean
  resumen: CargosListos | undefined
}) {
  const {t} = useTranslation()
  const table = useTableState('cargo')
  // La columna «Personas» se monta o no: ocultarla con CSS no sirve porque bajo `table-fixed` el
  // `<colgroup>` le reserva el ancho. En el teléfono el conteo viaja en la meta de la fila.
  const enTelefono = useIsMobile()
  const [filtro, setFiltro] = useState<FiltroCargos>('todos')
  const cargos = useCargosListos(businessId, {
    page: table.page,
    search: table.search,
    sortBy: table.sortBy,
    sortDir: table.sortDir,
    // El recorte va al servidor (R6): filtrar lo ya traído mentiría sobre el total del pie.
    listo: filtro === 'todos' ? undefined : filtro === 'listo',
  })
  const [preparando, setPreparando] = useState<CargoListo | null>(null)
  // Sobrevive al cierre del diálogo: es la única lista de quién quedó sin solicitud.
  const [ultimoLote, setUltimoLote] = useState<{cargo: string; lote: LoteSolicitudes} | null>(null)

  const columnas: Column<CargoListo>[] = [
    {
      key: 'cargo',
      header: t('solicitudIps.columnas.cargo'),
      sortable: true,
      width: enTelefono ? undefined : 'w-1/2',
      cell: (cargo) => (
        <CeldaEstado
          chip={cargo.listo ? undefined : <Tag tone="amber">{t('solicitudIps.cargos.incompleto')}</Tag>}
          nombre={cargo.cargo}
          meta={
            [
              cargo.listo ? null : t('solicitudIps.cargos.faltaPerfil'),
              enTelefono ? t('solicitudIps.cargos.personas', {count: cargo.personas}) : null,
            ]
              .filter(Boolean)
              .join(' · ') || undefined
          }
          porque={
            cargo.listo
              ? undefined
              : t('solicitudIps.cargos.falta', {
                  campos: cargo.faltantes.map((clave) => t(`solicitudIps.falta.${clave}`)).join(', '),
                })
          }
        />
      ),
    },
    ...(enTelefono
      ? []
      : [
          {
            key: 'personas',
            header: t('solicitudIps.columnas.personas'),
            sortable: true,
            width: 'w-32',
            cell: (cargo: CargoListo) => (
              <span className="text-xs text-muted-foreground">
                {t('solicitudIps.cargos.personas', {count: cargo.personas})}
              </span>
            ),
          },
        ]),
    {
      key: 'preparar',
      header: '',
      width: enTelefono ? 'w-40' : 'w-44',
      className: 'text-right',
      stopClick: true,
      cell: (cargo) => (
        <CeldaActo
          acto={
            cargo.listo && cargo.personas > 0 ? (
              <Button
                size="sm"
                data-testid={`solicitudIps-preparar-${cargo.position_id}`}
                disabled={!puedeFirmar}
                onClick={() => setPreparando(cargo)}>
                {t('solicitudIps.cargos.preparar')}
              </Button>
            ) : undefined
          }
          secundario={
            !cargo.listo ? (
              <Link
                className="text-xs underline"
                data-testid={`solicitudIps-al-perfil-${cargo.position_id}`}
                to={`/businesses/${businessId}/sgsst/perfiles-cargo/${cargo.position_id}`}>
                {t('solicitudIps.cargos.alPerfil')}
              </Link>
            ) : cargo.personas === 0 ? (
              // Un cargo vacante no ofrece generar: serían 0 solicitudes con mensaje de éxito.
              <span className="text-xs text-muted-foreground" data-testid={`solicitudIps-vacante-${cargo.position_id}`}>
                {t('solicitudIps.cargos.sinTitulares')}
              </span>
            ) : undefined
          }
        />
      ),
    },
  ]

  // `conteo: undefined` pinta el rótulo sin cifra; `null` esconde la opción.
  const opciones: OpcionEstado<FiltroCargos>[] = [
    {
      valor: 'todos',
      etiqueta: t('solicitudIps.cargos.filtroTodos'),
      conteo: resumen && resumen.listos + resumen.incompletos,
    },
    {valor: 'listo', etiqueta: t('solicitudIps.cargos.filtroListos'), conteo: resumen?.listos},
    {valor: 'incompletos', etiqueta: t('solicitudIps.cargos.filtroIncompletos'), conteo: resumen?.incompletos},
  ]

  if (cargos.isError) return <ErrorDeCarga onReintentar={() => void cargos.refetch()} />

  return (
    <div className="space-y-4" data-testid="solicitudIps-cargos">
      <ListCard
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput
              value={table.searchInput}
              onChange={table.setSearchInput}
              placeholder={t('solicitudIps.cargos.buscar')}
              data-testid="solicitudIps-buscar-cargo"
            />
            <div role="group" data-testid="solicitudIps-resumen" aria-label={t('solicitudIps.cargos.filtroAria')}>
              <FiltrosDeEstado
                opciones={opciones}
                valor={filtro}
                onValor={(siguiente) => {
                  setFiltro(siguiente)
                  table.setPage(1)
                }}
                testIdPrefijo="solicitudIps-filtro"
              />
            </div>
          </div>
        }
        footer={
          (cargos.data?.total ?? 0) > 0 && (
            <PaginationBar
              page={table.page}
              pageSize={CARGOS_PAGE_SIZE}
              total={cargos.data!.total}
              onPageChange={table.setPage}
            />
          )
        }>
        <DataTable
          columns={columnas}
          rows={cargos.data?.items ?? []}
          rowKey={(cargo) => cargo.position_id}
          rowTestId={(cargo) => `solicitudIps-cargo-${cargo.position_id}`}
          sortBy={table.sortBy}
          sortDir={table.sortDir}
          onSort={table.toggleSort}
          emptyState={<ListEmptyState title={t('solicitudIps.cargos.vacio')} />}
        />
      </ListCard>

      {ultimoLote && (
        <div className="space-y-1 text-sm" data-testid="solicitudIps-ultimo-lote">
          <p>
            {t('solicitudIps.cargos.ultimoLote', {
              cargo: ultimoLote.cargo,
              count: ultimoLote.lote.generadas.length,
            })}
          </p>
          {ultimoLote.lote.no_generadas.map((fila) => (
            <p key={fila.employee_id} className="text-muted-foreground">
              {t('solicitudIps.preparar.noGenerada', {
                nombre: fila.trabajador ?? '',
                motivo: translateDetail(fila.motivo),
              })}
            </p>
          ))}
        </div>
      )}

      <PrepararDialog
        businessId={businessId}
        cargo={preparando}
        onClose={() => setPreparando(null)}
        onGenerado={(lote) => setUltimoLote({cargo: preparando?.cargo ?? '', lote})}
      />
    </div>
  )
}

export function SolicitudesIpsPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {business, guard} = useOperatorPage(businessId)
  const capabilities = useCapabilities(businessId)
  const godReadOnly = useGodModeReadOnly()
  const resumen = useResumenCargos(business ? businessId : undefined)
  const [searchParams, setSearchParams] = useSearchParams()

  if (guard) return guard

  // Los endpoints exigen `require_paid_business`: sin esta puerta un tenant gratuito recibe 402 sin mensaje.
  if (capabilities.plan_tier === 'free') {
    return (
      <PlanLockedPage
        feature={t('plans.feature.solicitudesIps')}
        businessId={businessId}
        businessName={business!.name}
      />
    )
  }

  const tab = parseTab(searchParams.get('tab')) ?? 'solicitudes'
  function setTab(next: TabKey) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }

  const puedeFirmar = capabilities.sgsst_firmar && !godReadOnly
  const razonBloqueo = godReadOnly
    ? t(capabilities.sgsst_firmar ? 'solicitudIps.bloqueoSeguro' : 'solicitudIps.bloqueoModoDios')
    : capabilities.firmar_bloqueado_por

  return (
    <div className="space-y-5" data-testid="solicitudes-ips-page">
      <PageHeading
        title={t('solicitudIps.title')}
        subtitle={business!.name}
        ayuda={
          <HelpIcon label={t('common.help.iconLabelFor', {campo: t('solicitudIps.title')})} testId="solicitudIps-intro">
            {t('solicitudIps.intro')}
          </HelpIcon>
        }
        // El sidebar solo enlaza `/ips`: sin este enlace esta pantalla no es alcanzable.
        accion={
          <Button asChild size="sm" variant="outline" data-testid="solicitudIps-a-ips">
            <Link to={`/businesses/${businessId}/ips`}>{t('solicitudIps.aIps')}</Link>
          </Button>
        }
      />

      {!puedeFirmar && razonBloqueo && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="solicitudIps-bloqueo">
          {razonBloqueo}
        </p>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="solicitudes" data-testid="tab-solicitudes">
            {t('solicitudIps.tabs.solicitudes')}
          </TabsTrigger>
          <TabsTrigger value="preparar" data-testid="tab-preparar">
            <span className="inline-flex items-center gap-1.5">
              {t('solicitudIps.tabs.preparar')}
              {!!resumen.data?.listos && (
                <Tag tone="green" data-testid="tab-preparar-chip">
                  {resumen.data.listos}
                </Tag>
              )}
            </span>
          </TabsTrigger>
          <TabsTrigger value="plazos" data-testid="tab-plazos">
            {t('solicitudIps.tabs.plazos')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="solicitudes" className="pt-4">
          <SolicitudesTab businessId={businessId!} puedeFirmar={puedeFirmar} />
        </TabsContent>

        <TabsContent value="preparar" className="pt-4">
          <PrepararTab businessId={businessId!} puedeFirmar={puedeFirmar} resumen={resumen.data} />
        </TabsContent>

        <TabsContent value="plazos" className="pt-4">
          {resumen.isError ? (
            <ErrorDeCarga onReintentar={() => void resumen.refetch()} />
          ) : (
            resumen.data && (
              <Plazos
                businessId={businessId!}
                vence={{
                  corto: resumen.data.plazos.vence_corto_si_sale_hoy,
                  largo: resumen.data.plazos.vence_largo_si_sale_hoy,
                }}
                puedeFijar={puedeFirmar}
              />
            )
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
